import { useEffect, useState } from "react";
import { FileText, Download, ExternalLink } from "lucide-react";
import type { Receipt } from "../../lib/receipts/types";
import { receiptStore } from "../../lib/receipts";

interface Props {
  receipt: Receipt;
  alt?: string;
  className?: string;
  testId?: string;
}

const BROKEN_FALLBACK_SVG =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4K";

function isPdf(receipt: Receipt): boolean {
  if (receipt.mimeType?.toLowerCase() === "application/pdf") return true;
  return receipt.filename?.toLowerCase().endsWith(".pdf") ?? false;
}

export function ReceiptPreview({ receipt, alt, className, testId }: Props) {
  // Local URL state so we can swap in a refreshed signed URL on error
  // without mutating the global store snapshot from a render.
  const [url, setUrl] = useState(receipt.url);
  const [retried, setRetried] = useState(false);
  const [didError, setDidError] = useState(false);

  useEffect(() => {
    setUrl(receipt.url);
    setRetried(false);
    setDidError(false);
  }, [receipt.id, receipt.url]);

  const handleError = async () => {
    if (retried) {
      setDidError(true);
      return;
    }
    setRetried(true);
    const fresh = await receiptStore.refreshUrl(receipt.id);
    if (fresh && fresh !== url) {
      setUrl(fresh);
    } else {
      setDidError(true);
    }
  };

  if (isPdf(receipt)) {
    // Browsers render PDFs inline via <embed>/<object>/<iframe>. We use
    // <object> + a link fallback so screen readers and unsupported viewers
    // still get a download path.
    return (
      <div
        data-testid={testId}
        data-receipt-kind="pdf"
        className={`relative w-full h-full ${className ?? ""}`}
      >
        <object
          data={`${url}#toolbar=0&navpanes=0`}
          type="application/pdf"
          className="w-full h-full"
          aria-label={alt ?? receipt.filename}
        >
          <div className="flex flex-col items-center justify-center gap-2 p-6 text-slate-500 text-center h-full">
            <FileText className="w-8 h-8" />
            <div className="text-sm font-medium">PDF preview unavailable</div>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> Open in new tab
            </a>
          </div>
        </object>
        <a
          href={url}
          download={receipt.filename}
          aria-label="Download PDF receipt"
          data-testid="receipt-preview-download"
          className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-white/90 backdrop-blur px-2 py-1 text-xs font-medium text-slate-700 shadow-sm border border-slate-200 hover:bg-white"
        >
          <Download className="w-3 h-3" /> PDF
        </a>
      </div>
    );
  }

  if (didError) {
    return (
      <div
        data-testid={testId}
        data-receipt-kind="error"
        className={`flex flex-col items-center justify-center gap-2 bg-slate-100 text-slate-400 ${className ?? ""}`}
      >
        <img
          src={BROKEN_FALLBACK_SVG}
          alt={alt ?? "Receipt failed to load"}
          width={64}
          height={64}
        />
        <a
          href={url || receipt.url}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-blue-600 hover:underline"
        >
          Open original
        </a>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt ?? receipt.filename}
      data-testid={testId}
      data-receipt-kind="image"
      className={className}
      onError={handleError}
    />
  );
}
